import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import config from '../config';

const API_URL = config.API_URL;

/**
 * Hook pour gérer la visibilité des modules du dashboard par client
 * La configuration est persistée en base de données
 *
 * @param {string} clientId - ID du client (Client.id)
 * @param {string} userRole - Rôle de l'utilisateur (ADMIN, USER, CLIENT)
 * @returns {Object} { isModuleVisible, toggleModuleVisibility, setModuleVisibility, loading, hiddenModules }
 */
export const useClientModuleVisibility = (clientId, userRole) => {
  const [visibility, setVisibility] = useState({});
  const [loading, setLoading] = useState(true);

  // Charger la configuration au montage ou quand clientId change
  useEffect(() => {
    const loadConfig = async () => {
      if (!clientId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const token = localStorage.getItem('kokpeet_token');
        const response = await axios.get(
          `${API_URL}/api/client-dashboard-config/${clientId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          setVisibility(response.data.visibility || {});
        }
      } catch (error) {
        console.error('Erreur chargement config visibilité:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [clientId]);

  // Vérifie si un module est visible
  const isModuleVisible = useCallback((moduleId) => {
    // Par défaut, tout est visible (si pas de config, le module est visible)
    return visibility[moduleId] !== false;
  }, [visibility]);

  // Toggle la visibilité d'un module
  const toggleModuleVisibility = useCallback(async (moduleId) => {
    if (!clientId || userRole === 'CLIENT') {
      return;
    }

    const newIsVisible = !isModuleVisible(moduleId);

    // Optimistic update
    setVisibility(prev => ({
      ...prev,
      [moduleId]: newIsVisible
    }));

    try {
      const token = localStorage.getItem('kokpeet_token');
      await axios.post(
        `${API_URL}/api/client-dashboard-config/${clientId}/${moduleId}`,
        { isVisible: newIsVisible },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Erreur toggle visibilité:', error);
      // Rollback en cas d'erreur
      setVisibility(prev => ({
        ...prev,
        [moduleId]: !newIsVisible
      }));
    }
  }, [clientId, userRole, isModuleVisible]);

  // Set explicite de la visibilité
  const setModuleVisibility = useCallback(async (moduleId, isVisible) => {
    if (!clientId || userRole === 'CLIENT') return;

    // Optimistic update
    setVisibility(prev => ({
      ...prev,
      [moduleId]: isVisible
    }));

    try {
      const token = localStorage.getItem('kokpeet_token');
      await axios.post(
        `${API_URL}/api/client-dashboard-config/${clientId}/${moduleId}`,
        { isVisible },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Erreur mise à jour visibilité:', error);
      // Rollback en cas d'erreur
      setVisibility(prev => ({
        ...prev,
        [moduleId]: !isVisible
      }));
    }
  }, [clientId, userRole]);

  // Liste des modules masqués
  const hiddenModules = useMemo(() => {
    return Object.entries(visibility)
      .filter(([_, isVisible]) => isVisible === false)
      .map(([moduleId]) => moduleId);
  }, [visibility]);

  return {
    isModuleVisible,
    toggleModuleVisibility,
    setModuleVisibility,
    loading,
    hiddenModules,
    visibility
  };
};

export default useClientModuleVisibility;
